import { useEffect, useState } from "react";
import BoutonFlottant from "@/components/BoutonFlottant";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS } from "@/lib/constants";
import { useAuth } from "@/lib/hooks/useAuth";
import {
  startOrderAlarm,
  stopOrderAlarm,
  unlockOrderAlarm,
  sonAutorise,
  testerOrderAlarm,
} from "@/lib/utils/order-alarm";

/**
 * Le fournisseur n'entend PAS le meme son que l'admin.
 *
 * Quand les deux sont dans la meme piece, un son identique ne dit pas QUI
 * doit agir : les deux se levent, ou aucun. Celui-ci est une sonnette de
 * porte — deux notes descendantes. Chez l'admin c'est une vente qui entre,
 * ici c'est quelqu'un qui apporte du travail.
 */
const TIMBRE = "fournisseur" as const;

type PanierArrive = {
  id: string;
  code: string;
};

/**
 * L'alerte sonore du fournisseur, a l'arrivee d'un panier a preparer.
 *
 * Pourquoi un composant a part, et pas `NewOrderAlarm` :
 *
 *   - `NewOrderAlarm` ecoute la collection `orders`, que les regles
 *     Firestore refusent au fournisseur. L'ecoute echouerait en silence et
 *     il croirait avoir une alarme.
 *   - une commande ne le concerne pas forcement. Ce qui le concerne, c'est
 *     SON panier.
 *
 * A placer dans FournisseurLayout : l'alerte doit fonctionner quelle que
 * soit la page ouverte, pas seulement le tableau de bord.
 */
export default function NewBasketAlarm() {
  const { user } = useAuth();
  const [fournisseurId, setFournisseurId] = useState<string | null>(null);
  const [arrivees, setArrivees] = useState<PanierArrive[]>([]);
  const [sonPret, setSonPret] = useState(false);

  // Les navigateurs refusent de jouer un son avant toute interaction : on
  // deverrouille au premier clic ou a la premiere touche.
  useEffect(() => {
    const debloquer = () => {
      unlockOrderAlarm();
      window.setTimeout(() => setSonPret(sonAutorise()), 150);
    };
    window.addEventListener("pointerdown", debloquer);
    window.addEventListener("keydown", debloquer);

    const minuterie = window.setInterval(() => setSonPret(sonAutorise()), 3000);

    return () => {
      window.removeEventListener("pointerdown", debloquer);
      window.removeEventListener("keydown", debloquer);
      clearInterval(minuterie);
    };
  }, []);

  // Sa fiche fournisseur : c'est son identifiant, pas celui de son compte,
  // qui figure sur les paniers.
  useEffect(() => {
    if (!user?.uid) return;
    let annule = false;

    (async () => {
      try {
        const { getDocuments } = await import("@/lib/firebase/firestore");
        const fiches = await getDocuments(COLLECTIONS.FOURNISSEURS, [
          { field: "userId", operator: "==", value: user.uid },
        ]);
        if (!annule) setFournisseurId(fiches.length > 0 ? fiches[0].id : null);
      } catch (e: any) {
        console.error("[ALARME PANIERS] fiche introuvable :", e?.message);
      }
    })();

    return () => {
      annule = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!fournisseurId) return;

    // Filtre sur SES paniers, statut PENDING : ceux qui lui sont attribues
    // et qu'il n'a pas encore prepares. Cette requete utilise l'index qui
    // existe deja (fournisseurId, status, createdAt) — rien a creer, donc
    // aucun risque d'alarme muette en attendant un index.
    const q = query(
      collection(db, COLLECTIONS.SUPPLIER_BASKETS),
      where("fournisseurId", "==", fournisseurId),
      where("status", "==", "PENDING"),
      orderBy("createdAt", "desc"),
    );

    // Firestore livre tout le contenu initial comme des « ajouts ». Sans
    // ce drapeau, ouvrir la page ferait sonner l'alarme pour chaque panier
    // deja en attente.
    let premierEnvoi = true;

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // Le premier envoi n'est PLUS ignore.
        //
        // Avant, il l'etait pour ne pas sonner a chaque ouverture de page.
        // Mais un panier deja en attente quand le fournisseur arrive est
        // justement celui qu'il faut lui signaler : c'est celui qui attend
        // depuis le plus longtemps.
        void premierEnvoi;

        // L'ALARME SUIT L'ETAT, PAS LES CLICS.
        //
        // Avant, elle sonnait a l'arrivee d'un panier et se taisait des que
        // le fournisseur cliquait « J'ai vu » — sans avoir rien commence. La
        // commande pouvait dormir en silence.
        //
        // Maintenant : la requete ecoutee ne contient que les paniers EN
        // ATTENTE. Tant qu'il en reste un, ca sonne. Quand le dernier passe
        // en preparation, il quitte la requete et le son s'arrete tout seul.
        //
        // Personne ne « coupe » l'alarme : on la fait disparaitre en
        // commencant le travail.
        const enAttente = snapshot.docs.map((doc: any) => {
          const d: any = doc.data();
          return {
            id: doc.id,
            code: String(d.orderCode || d.orderId || doc.id).slice(0, 20),
          };
        });

        if (enAttente.length === 0) {
          stopOrderAlarm();
          setArrivees([]);
          return;
        }

        startOrderAlarm(TIMBRE);
        setArrivees(enAttente);
      },
      (err) => {
        // Une erreur de droits ou de reseau ne doit jamais casser la page.
        console.error("[ALARME PANIERS] Ecoute interrompue :", err?.message);
      },
    );

    return () => {
      unsubscribe();
      stopOrderAlarm();
    };
  }, [fournisseurId]);

  /**
   * Commencer la preparation, depuis le bandeau d'alarme.
   *
   * L'ancien « J'ai vu » coupait le son sans rien faire d'autre. Ce bouton
   * enregistre vraiment le depart, et le son s'arrete parce que le panier
   * quitte la liste des « en attente » — pas parce qu'on a clique.
   *
   * En cas d'echec, le son CONTINUE. C'est voulu : le panier n'a pas ete
   * commence, il n'y a aucune raison de faire comme si.
   */
  const [enCours, setEnCours] = useState(false);

  const commencerTout = async () => {
    setEnCours(true);
    try {
      const { api } = await import("@/lib/firebase/api-client");
      // Tous les paniers affiches d'un coup : le bandeau annonce leur
      // nombre, donc rien n'est demarre a son insu.
      await Promise.all(arrivees.map((p) => api.baskets.commencer(p.id)));
      // On ne touche ni au son ni a la liste : l'ecoute Firestore s'en
      // charge des que l'enregistrement est visible.
    } catch (e: any) {
      console.error("[ALARME PANIERS] Impossible de commencer :", e?.message);
    } finally {
      setEnCours(false);
    }
  };

  // Rien en attente : on affiche seulement l'etat du son. Un bandeau tant
  // que le navigateur bloque, puis un petit bouton de test discret.
  // Personne ne doit decouvrir a midi que le son etait coupe depuis le
  // matin.
  if (arrivees.length === 0) {
    if (!sonPret) {
      return (
        <div className="fixed inset-x-0 top-0 z-[10000] px-3 pt-3">
          <button
            type="button"
            onClick={() => {
              testerOrderAlarm(TIMBRE);
              window.setTimeout(() => setSonPret(sonAutorise()), 200);
            }}
            className="mx-auto flex w-full max-w-3xl items-center justify-center gap-2 rounded-2xl border-2 border-amber-500 bg-amber-100 px-4 py-3 text-sm font-bold text-amber-900 shadow-lg hover:bg-amber-200"
          >
            🔇 Le son est bloqué — touche l'écran pour entendre tes commandes
          </button>
        </div>
      );
    }

    // DEPLACABLE DU DOIGT. Fixe, il recouvrait la barre de navigation du
    // bas sur telephone — et il n'existe pas de place qui convienne a tous
    // les ecrans. La personne le met ou elle veut, et ca reste.
    return (
      <BoutonFlottant
        cle="tester-son"
        onClick={() => testerOrderAlarm(TIMBRE)}
        title="Jouer le son pour vérifier qu'il fonctionne — glisse-le pour le déplacer"
        className="fixed bottom-24 right-3 z-[9999] rounded-full border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-600 shadow hover:bg-gray-50 md:bottom-3"
      >
        🔔 Tester le son
      </BoutonFlottant>
    );
  }

  return (
    <div className="fixed inset-x-0 top-0 z-[10000] px-3 pt-3">
      <div className="mx-auto max-w-3xl rounded-2xl border-2 border-red-500 bg-red-600 text-white shadow-2xl">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-lg font-extrabold leading-tight">
              {arrivees.length === 1
                ? "NOUVELLE COMMANDE À PRÉPARER"
                : `${arrivees.length} COMMANDES À PRÉPARER`}
            </p>
            <p className="mt-0.5 truncate text-sm text-red-100">
              {arrivees
                .slice(-3)
                .map((p) => p.code)
                .join("  ·  ")}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* « Voir » ne coupe plus le son : regarder n'est pas
                commencer. Le son s'arretera quand le panier changera
                vraiment d'etat. */}
            <Link
              to="/fournisseur/dashboard"
              className="rounded-xl bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50"
            >
              Voir
            </Link>
            {/* A la place de « Arreter le son » : le seul geste qui le fait
                taire, et qui fait aussi le vrai travail. Le compte est
                affiche pour qu'on sache combien de paniers demarrent. */}
            <button
              type="button"
              disabled={enCours}
              onClick={() => void commencerTout()}
              className="rounded-xl border-2 border-white bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/20 disabled:opacity-60"
            >
              {enCours
                ? "..."
                : arrivees.length > 1
                  ? `COMMENCER (${arrivees.length})`
                  : "COMMENCER"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
